import Map from "@churchofjesuschrist/maps-google-map";
import ServiceOverlay from "@churchofjesuschrist/maps-google-service-overlay";

export default function mapDirective($q, $translate, google, utils, MapService, MapDrawService, DataService, DataConst) {
	"ngInject";

	// map boundaries and labels
	const overlayEquals = (a, b) => (
		a === b || (a && b && a.key === b.key)
	);

	const getOverlay = (find, overlays) => (
		overlays ? overlays.filter((overlay) => (overlayEquals(overlay, find)))[0] : null
	);

	const loadUnitDetails = (orgs) => {
		let promises = [],
			genericUnitIds = orgs
				.filter((org) => (DataConst.GENERIC_UNIT_ORG_TYPES.indexOf(org.type) !== -1))
				.map((unit) => (unit.id));
		if (genericUnitIds.length) {
			// download location details for unit types that need color
			promises.push(DataService.loadUnitDetails(genericUnitIds));
		}

		return $q.all(promises);
	};

	const generateOrgOverlays = (orgs, showLabels) => {
		let labels = [],
			dynamics = [],
			polygons = [],
			markers = [];
		if (orgs.length) {
			let dynamicOrgs = [];

			orgs.forEach((org) => {
				let hasBoundary = DataService.hasBoundary(org),
					isFacility = DataService.isFacility(org);
				if (hasBoundary || isFacility) {
					let key = MapService.serializeIdentity(org);

					if (showLabels) {
						labels.push({key, org});
					}

					if (hasBoundary) {
						if (org.boundary) {
							polygons.push({key, org});
						} else {
							dynamicOrgs.push(org);
						}
					} else if (isFacility) {
						markers.push({key, org});
					}
				}
			});

			if (dynamicOrgs.length) {
				let key = dynamicOrgs.sort(utils.sortOrgs)
					.map(MapService.serializeIdentity).join("|");
				dynamics.push({key, orgs: dynamicOrgs});
			}
		}

		return {labels, dynamics, polygons, markers};
	};

	const removeOrgOverlay = (key, overlayGroup, property) => {
		if (key && overlayGroup) {
			let group = overlayGroup[property];
			if (group) {
				for (let i = group.length - 1; i >= 0; i--) {
					if (group[i].key === key) {
						group.splice(i, 1);
						break;
					}
				}
			}
		}
	};

	const generateOverlays = (boundaryOpts, labelOpts) => {
		// 1. get metadata of how map should look
		let overlays = {},
			promises = [],
			opts = DataService.getMapOpts(),
			automatic = boundaryOpts.automatic,
			types = boundaryOpts.types,
			showLabels = labelOpts.active;

		// selected org
		if (opts.selectedOrg && DataService.hasBoundary(opts.selectedOrg)) {
			let key = MapService.serializeIdentity(opts.selectedOrg);
			overlays.selected = {key, org: opts.selectedOrg};
		}

		// static orgs
		if (opts.staticOrgs && opts.staticOrgs.length) {
			promises.push(loadUnitDetails(opts.staticOrgs).then(() => {
				overlays.static = generateOrgOverlays(opts.staticOrgs, showLabels);
			}));
		}

		// automatic orgs
		if (automatic && opts.automaticOrgs && opts.automaticOrgs.length) {
			promises.push(loadUnitDetails(opts.automaticOrgs).then(() => {
				overlays.automatic = generateOrgOverlays(opts.automaticOrgs, showLabels);
			}));
		}

		// opted orgs
		if (!automatic && types && types.length) {
			promises.push(DataService.getOrgsByTypes(types).then((orgs) => {
				overlays.opted = generateOrgOverlays(orgs, showLabels);
			}));
		}

		return $q.all(promises).then(() => {
			// remove any opted/automatic that are also in static
			if (overlays.static) {
				overlays.static.labels.forEach((overlay) => {
					removeOrgOverlay(overlay.key, overlays.automatic, "labels");
					removeOrgOverlay(overlay.key, overlays.opted, "labels");
				});
				overlays.static.polygons.forEach((overlay) => {
					removeOrgOverlay(overlay.key, overlays.automatic, "polygons");
					removeOrgOverlay(overlay.key, overlays.opted, "polygons");
				});
			}

			// if selected is also in another category, then boundary only on selected but label can stay
			if (overlays.selected) {
				removeOrgOverlay(overlays.selected.key, overlays.static, "polygons");
				removeOrgOverlay(overlays.selected.key, overlays.automatic, "polygons");
				removeOrgOverlay(overlays.selected.key, overlays.opted, "polygons");
			}

			return overlays;
		});
	};

	const removeOverlayGroup = (newOverlayGroup, oldOverlayGroup, property) => {
		let newGroup = newOverlayGroup ? newOverlayGroup[property] : null,
			oldGroup = oldOverlayGroup ? oldOverlayGroup[property] : null;
		if (oldGroup) {
			oldGroup.forEach((oldOverlay) => {
				let newOverlay = getOverlay(oldOverlay, newGroup);
				if (newOverlay) {
					// leave same alone
					newOverlay.overlay = oldOverlay.overlay;
				} else {
					// delete old
					MapDrawService.removeOverlays(oldOverlay.overlay);
				}
			});
		}
	};

	const addOverlayGroup = (newOverlayGroup, oldOverlayGroup, property, addFn) => {
		let newGroup = newOverlayGroup ? newOverlayGroup[property] : null,
			oldGroup = oldOverlayGroup ? oldOverlayGroup[property] : null;
		if (newGroup) {
			newGroup.forEach((newOverlay) => {
				let oldOverlay = getOverlay(newOverlay, oldGroup);
				if (!oldOverlay) {
					// add new
					newOverlay.overlay = addFn(newOverlay.org || newOverlay.orgs);
				}
			});
		}
	};

	const removeOverlayOpt = (newOverlays, oldOverlays, refreshLabels, opt) => {
		let newOpt = newOverlays[opt],
			oldOpt = oldOverlays[opt];

		removeOverlayGroup(refreshLabels ? null : newOpt, oldOpt, "labels");
		removeOverlayGroup(newOpt, oldOpt, "dynamics");
		removeOverlayGroup(newOpt, oldOpt, "polygons");
		removeOverlayGroup(newOpt, oldOpt, "markers");
	};

	const addOverlayOpt = (newOverlays, oldOverlays, refreshLabels, opt, map, prioritize) => {
		let newOpt = newOverlays[opt],
			oldOpt = oldOverlays[opt];

		addOverlayGroup(newOpt, refreshLabels ? null : oldOpt, "labels", (org) => (
			MapDrawService.drawOrgLabel(org, map, prioritize)
		));
		addOverlayGroup(newOpt, oldOpt, "dynamics", (orgs) => (
			new ServiceOverlay({
				ids: orgs.map(MapService.getIdentity),
				zIndex: 2,
				map
			})
		));
		addOverlayGroup(newOpt, oldOpt, "polygons", (org) => (
			MapDrawService.drawOrgBoundary(org, map, prioritize)
		));
		addOverlayGroup(newOpt, oldOpt, "markers", (org) => (
			MapDrawService.drawOrgFacility(org, map, prioritize)
		));
	};

	const updateOverlays = (newOverlays, oldOverlays, map, refreshLabels) => {
		MapDrawService.drawOrgHighlighted();
		// remove old overlays no longer current
		let updateSelected = !overlayEquals(newOverlays.selected, oldOverlays.selected);
		if (oldOverlays.selected) {
			if (updateSelected) {
				MapDrawService.removeOverlays(oldOverlays.selected.overlay);
			} else if (newOverlays.selected) {
				newOverlays.selected.overlay = oldOverlays.selected.overlay;
			}
		}
		removeOverlayOpt(newOverlays, oldOverlays, refreshLabels, "static");
		removeOverlayOpt(newOverlays, oldOverlays, refreshLabels, "automatic");
		removeOverlayOpt(newOverlays, oldOverlays, refreshLabels, "opted");

		// add new current overlays
		if (newOverlays.selected && updateSelected) {
			newOverlays.selected.overlay = MapDrawService.drawOrgSelected(newOverlays.selected.org, map);
		}
		addOverlayOpt(newOverlays, oldOverlays, refreshLabels, "static", map, true);
		addOverlayOpt(newOverlays, oldOverlays, refreshLabels, "automatic", map);
		addOverlayOpt(newOverlays, oldOverlays, refreshLabels, "opted", map);

		return newOverlays;
	};


	return {
		restrict: "E",
		replace: true,
		templateUrl: "./components/map/map-template.html",
		scope: {
			onLoad: "&"
		},
		link: (scope, elem) => {
			scope.boundaryOpts = {};
			scope.labelOpts = {};
			MapService.loadMapsApi().then(() => {
				if (!scope.$$destroyed) {
					const map = new Map(elem.find(".map__canvas").get(0), {
						center: {lat: 0, lng: 0},
						zoom: 2,
						gestureHandling: "greedy"
					});
					scope.overlays = {};
					scope.fetchingItems = [];
					scope.updateOverlays = () => {
						scope.showSpinner = true;
						let currentFetch = scope.fetching = generateOverlays(scope.boundaryOpts, scope.labelOpts);
						currentFetch.id = Date.now();
						scope.fetchingItems.push(currentFetch.id);
						currentFetch.then((overlays) => {
							const index = scope.fetchingItems.indexOf(currentFetch.id);
							if (index > -1) {
								scope.fetchingItems.splice(index, 1);
							}
							//spinner check - only remove spinner of the scope fetchingItems is empty meaning
							//   all of the fetches are finished fetching
							scope.showSpinner = scope.fetchingItems.length !== 0;
							let labelsClean = DataService.getMapOpts().labelsClean,
								refreshLabels = labelsClean !== scope.labelsClean;
							scope.overlays = updateOverlays(overlays, scope.overlays, map, refreshLabels);
							scope.labelsClean = labelsClean;
						});
					};
					scope.$on("mapOptsChanged", scope.updateOverlays);

					if (scope.onLoad) {
						let listener = map.addListener("bounds_changed", () => {
							google.maps.event.removeListener(listener);
							scope.onLoad({map});
						});
					}
				}
			});
		}
	};
}
